import { DisabledInfo, DisabledReason } from './types';
import { ATTR } from './attrs';
import { uiText } from './uiText';

/** Spread onto a disabled control: `<Button disabled {...helpWhy(reason)} />` (or pass `why` to HelpTag). */
export function helpWhy(reason: DisabledReason | null | undefined): Record<string, string> {
    return reason ? { [ATTR.why]: JSON.stringify(reason) } : {};
}

const KINDS = ['missing-key', 'status', 'module-off', 'plan-limit', 'custom'];

export function parseWhy(raw: string | null): DisabledReason | null {
    if (!raw) return null;
    try {
        const v = JSON.parse(raw);
        return v && KINDS.includes(v.kind) ? (v as DisabledReason) : null;
    } catch {
        return null;
    }
}

const probe = (e: Element | null): boolean =>
    !!e && (e.matches(':disabled') || e.getAttribute('aria-disabled') === 'true' || e.classList.contains('Mui-disabled'));

export function isDisabledEl(el: HTMLElement): boolean {
    return probe(el) || probe(el.querySelector('button, input, select, textarea, [role="button"], a'));
}

export function readDisabled(el: HTMLElement): DisabledInfo | null {
    if (!isDisabledEl(el)) return null;
    const holder = el.hasAttribute(ATTR.why) ? el : el.querySelector(`[${ATTR.why}]`);
    return { reason: parseWhy(holder?.getAttribute(ATTR.why) ?? null) };
}

/** Plain-language "Why can't I?" text from the reason the screen supplied. `holders` = role NAMES, never people. */
export function describeWhy(info: DisabledInfo, holders: string[]): string {
    const r = info.reason;
    if (!r) return uiText('whyUnknown');
    switch (r.kind) {
        case 'missing-key': {
            const base = uiText('whyMissingKey');
            return holders.length ? `${base} ${uiText('whyAskRoles', { roles: holders.join(', ') })}` : `${base} ${uiText('whyAskAdmin')}`;
        }
        case 'status':
        case 'custom':
            return r.message;
        case 'module-off':
            return r.module ? uiText('whyModuleOff', { module: r.module }) : uiText('whyThisModuleOff');
        case 'plan-limit':
            return r.message ?? uiText('whyPlanLimit');
        default:
            return uiText('whyDefault');
    }
}

/** One short phrase for the "unavailable" line in an area card (no role lookup). */
export function shortWhy(info: DisabledInfo): string {
    const r = info.reason;
    if (!r) return uiText('shortUnknown');
    switch (r.kind) {
        case 'missing-key': return uiText('shortMissingKey');
        case 'status':
        case 'custom': return r.message;
        case 'module-off': return uiText('shortModuleOff');
        case 'plan-limit': return r.message ?? uiText('shortPlanLimit');
        default: return uiText('shortUnknown');
    }
}

/** Keys whose holders the card should look up for a reason. */
export function keysForReason(info: DisabledInfo | null, entryKeys: string[] | undefined): string[] {
    if (info?.reason?.kind === 'missing-key') return info.reason.keys ?? entryKeys ?? [];
    return [];
}
